import {
  StudyLayoutSkeleton,
  SuspensePageBoundary,
} from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { resolveRouteParam } from "@-/com.npmjs.expo-router";
import { View } from "@-/com.npmjs.react-native";
import { GlassButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type {
  LearnFlashcardExplorationDestination,
  LearnDeckStageValues,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { LearnFlashcardSwipePressHighlight } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import {
  RouteBodyStateScope,
  useRouteBodyState,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { MorphemePanels } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";
import { useRef } from "react";

function ExplorationPanelsSuspense({
  destination,
}: {
  destination: LearnFlashcardExplorationDestination;
}) {
  const { morpheme, prefix } = destination;
  const [activeIndex, setActiveIndex] = useRouteBodyState("exploration-active-index", 0);
  const viewNavigationRef = useRef(null);
  const requestedPanel = resolveRouteParam(prefix.params.view);
  const activePanel =
    requestedPanel === "info" || requestedPanel === "mnemonics"
      ? requestedPanel
      : prefix.href.endsWith("/mnemonics")
        ? "mnemonics"
        : "info";
  return (
    <MorphemePanels
      activeIndex={activeIndex}
      activePanel={activePanel}
      morpheme={morpheme}
      morphemeHrefObject={{ ...prefix, params: { ...prefix.params, morpheme } }}
      setActiveIndex={setActiveIndex}
      viewNavigationRef={viewNavigationRef}
    />
  );
}

/**
 * A swipe of the card showing this exploration presses Back instead of committing the card, so
 * the pill takes the same pressed tint while `stage` is dragged past the release threshold.
 */
export function LearnFlashcardExploration({
  destination,
  onBack,
  stage,
}: {
  destination: LearnFlashcardExplorationDestination;
  onBack: () => void;
  stage: Pick<LearnDeckStageValues, "cardHeight" | "cardWidth" | "dragX" | "isDragging">;
}) {
  return (
    <View className="min-h-0 flex-1">
      <View className="min-h-0 flex-1">
        <RouteBodyStateScope
          key={destination.morpheme}
          name={`exploration:${destination.morpheme}`}
        >
          <SuspensePageBoundary fallback={<StudyLayoutSkeleton />} location={import.meta.location}>
            <ExplorationPanelsSuspense destination={destination} />
          </SuspensePageBoundary>
        </RouteBodyStateScope>
      </View>
      <View className="items-center bg-background py-2">
        <GlassButton
          accessibilityLabel="Back to Review"
          className="h-11 rounded-full px-6 py-0"
          onPress={onBack}
          testID="explore-back-to-review"
        >
          <LearnFlashcardSwipePressHighlight
            className="rounded-full"
            direction="both"
            stage={stage}
            testID="explore-back-to-review-swipe-highlight"
          />
          <Text className="text-base leading-6" colorVariant="default">
            Back to Review
          </Text>
        </GlassButton>
      </View>
    </View>
  );
}
